import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import PrincipalContent from './PrincipalDashboard';
import { HQContent } from './HQDashboard';
import FeeDashboard from './FeeDashboard';

// School operations dashboard. IT tickets & Assets are LIVE from this portal
// (filterable by campus + period). Every other department is "coming soon".
const COLOR = {
  blue:'#2f6bff', red:'#e5484d', purple:'#8b5cf6', teal:'#0d9488',
  amber:'#d97706', coral:'#f2643f', green:'#16a34a', gray:'#64748b', pink:'#db2777',
};
const PERIODS = [['daily','Daily'],['weekly','Weekly'],['monthly','Monthly'],['yearly','Yearly']];

function Card({ icon, title, color, rows, live, comingSoon, onClick }) {
  const c = COLOR[color] || COLOR.gray;
  return (
    <div className="card" onClick={onClick}
      style={{ padding:'16px 18px', position:'relative', overflow:'hidden',
        cursor:onClick?'pointer':'default', transition:'transform .08s', opacity: comingSoon ? 0.72 : 1,
        boxShadow: onClick && live ? '0 0 0 1px '+c+'55' : undefined }}
      onMouseEnter={e => onClick && (e.currentTarget.style.transform='translateY(-2px)')}
      onMouseLeave={e => e.currentTarget.style.transform='none'}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
        <span style={{ width:30, height:30, borderRadius:8, background:c+'1a', color:c,
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{icon}</span>
        <span style={{ fontWeight:600, fontSize:14 }}>{title}</span>
        <span style={{ marginLeft:'auto', fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:10,
          background: live ? 'rgba(22,163,74,0.12)' : 'var(--bg3)', color: live ? 'var(--green)' : 'var(--text2)' }}>
          {live ? '● LIVE' : 'Coming soon'}
        </span>
      </div>
      {comingSoon ? (
        <div style={{ fontSize:12, color:'var(--text2)' }}>Connects to its source system later.</div>
      ) : rows.map((r, i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom: i<rows.length-1?7:0 }}>
          <span style={{ color:'var(--text2)' }}>{r[0]}</span>
          <span style={{ fontWeight:600, color: r[2] || 'var(--text)' }}>{r[1]}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Department detail (opened by clicking a live tile) ─────────────────────
function DeptDetail({ dept, isHOS, summary, onBack }) {
  const c = COLOR[dept.color] || COLOR.gray;
  const a = summary?.assets || {};
  return (
    <div>
      <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', fontSize:13, color:'var(--accent)', marginBottom:8, padding:0 }}>
        ◀ Dashboard
      </button>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:4 }}>
        <span style={{ width:38, height:38, borderRadius:10, background:c+'1a', color:c,
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{dept.icon}</span>
        <h1 style={{ fontSize:22, fontWeight:700, margin:0 }}>{dept.title}</h1>
        <span style={{ fontSize:10, fontWeight:600, padding:'3px 9px', borderRadius:10,
          background: dept.live ? 'rgba(22,163,74,0.12)' : 'var(--bg3)', color: dept.live ? 'var(--green)' : 'var(--text2)' }}>
          {dept.live ? '● LIVE' : 'Coming soon'}
        </span>
      </div>

      {dept.key === 'it' ? (
        <div style={{ marginTop:16 }}>{isHOS ? <PrincipalContent /> : <HQContent />}</div>
      ) : dept.key === 'finance' ? (
        <div style={{ marginTop:16 }}><FeeDashboard /></div>
      ) : dept.key === 'assets' ? (
        <div style={{ marginTop:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:14 }}>
            {[['Total assets', a.total, 'var(--text)'],['Active / in use', a.active, 'var(--green)'],
              ['In stock', a.stock, 'var(--accent)'],['Sent for service', a.in_service, 'var(--orange)'],
              ['Faulty', a.faulty, a.faulty>0?'var(--red)':'var(--text)'],['Scrapped', a.scrapped, 'var(--text2)']].map(([l,v,col],i) => (
              <div key={i} className="card" style={{ padding:'18px 20px' }}>
                <div style={{ fontSize:12, color:'var(--text2)', marginBottom:6 }}>{l}</div>
                <div style={{ fontSize:26, fontWeight:700, color:col }}>{v ?? '—'}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding:'18px 20px', marginTop:16 }}>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:6 }}>Coming soon</div>
          <div style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6 }}>
            Once <b>{dept.title}</b> is connected to its source system, this page will show the full breakdown —
            trends, per-campus comparison and the underlying records — the same way IT does today.
          </div>
        </div>
      )}
    </div>
  );
}

export default function CeoDashboard({ go }) {
  const { user } = useAuth();
  const isHOS = user?.role === 'principal';
  const myCampuses = (user?.campuses || []).map(c => c.name).join(', ');
  const [summary, setSummary] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [campus, setCampus] = useState('');
  const [dept, setDept] = useState(null);

  const load = useCallback(() => {
    api.get('/hq/mgmt-summary', { params: { period, ...(campus ? { campus_id: campus } : {}) } })
      .then(r => setSummary(r.data)).catch(()=>{});
  }, [period, campus]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!isHOS) api.get('/hq/campuses').then(r => setCampuses(r.data)).catch(()=>{}); }, [isHOS]);

  const it = summary?.it;
  const as = summary?.assets;
  const itRows = it ? [
    ['New (period)', it.newc, 'var(--accent)'],
    ['Open / pending', it.open, 'var(--amber)'],
    ['Critical', it.critical, it.critical>0?'var(--red)':'var(--text)'],
    ['Closed (period)', it.closed, 'var(--green)'],
    ['Campuses healthy', `${it.healthy}/${it.campuses}`],
  ] : [['New','…'],['Open','…'],['Critical','…']];
  const fe = summary?.fees;
  const inr = v => '₹' + Number(v || 0).toLocaleString('en-IN');
  const feeRows = fe && fe.total > 0 ? [
    ['Collected', `${fe.collected_pct}%`, 'var(--green)'],
    ['Total fee', inr(fe.total)],
    ['Paid', inr(fe.paid), 'var(--green)'],
    ['Pending', inr(fe.pending), fe.pending > 0 ? 'var(--red)' : 'var(--text)'],
    ['Students pending', fe.students_pending, fe.students_pending > 0 ? 'var(--red)' : 'var(--text)'],
  ] : [['No fee report uploaded', '—']];
  const assetRows = as ? [
    ['Total assets', as.total],
    ['Active / in use', as.active, 'var(--green)'],
    ['In stock', as.stock, 'var(--accent)'],
    ['Faulty', as.faulty, as.faulty>0?'var(--red)':'var(--text)'],
  ] : [['Total','…'],['Active','…']];

  const cards = [
    { key:'it',         icon:'💻', title:'IT Tickets',            color:'blue',  rows:itRows,    live:true },
    { key:'assets',     icon:'🖥️', title:'Assets',                color:'teal',  rows:assetRows, live:true },
    { key:'admissions', icon:'🎓', title:'Admissions & students', color:'blue',   comingSoon:true },
    { key:'safeguard',  icon:'🛡️', title:'Safeguarding',          color:'red',    comingSoon:true },
    { key:'academics',  icon:'📘', title:'Academics',             color:'purple', comingSoon:true },
    { key:'people',     icon:'👥', title:'People & culture',      color:'teal',   comingSoon:true },
    { key:'canteen',    icon:'🍽️', title:'Canteen',               color:'amber',  comingSoon:true },
    { key:'transport',  icon:'🚌', title:'Transport',             color:'coral',  comingSoon:true },
    { key:'safety',     icon:'📹', title:'Safety & surveillance', color:'red',    comingSoon:true },
    { key:'finance',    icon:'💰', title:'Fee Collection',        color:'green',  rows:feeRows,   live:true },
    { key:'licenses',   icon:'📜', title:'Licenses',              color:'gray',   comingSoon:true },
    { key:'marketing',  icon:'📣', title:'Marketing',             color:'pink',   comingSoon:true },
  ];

  if (dept) return <DeptDetail dept={dept} isHOS={isHOS} summary={summary} onBack={() => setDept(null)} />;

  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:18 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, margin:0 }}>
            🏫 {isHOS ? 'Campus Overview' : 'School Operations Dashboard'}
          </h1>
          <p style={{ fontSize:13, color:'var(--text2)', margin:'3px 0 0' }}>
            {isHOS && <><b>{myCampuses || 'Your campus'}</b> — </>}
            <b style={{ color:'var(--green)' }}>IT Tickets</b>, <b style={{ color:'var(--green)' }}>Assets</b> &amp; <b style={{ color:'var(--green)' }}>Fee Collection</b> are live; click any for the detailed view. Other departments are coming soon.
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <select value={period} onChange={e => setPeriod(e.target.value)} style={{ padding:'6px 8px' }}>
            {PERIODS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={campus} onChange={e => setCampus(e.target.value)} disabled={isHOS}
            title={isHOS ? 'Scoped to your campus' : undefined} style={{ padding:'6px 8px' }}>
            {isHOS ? <option>{myCampuses || 'My campus'}</option> : (
              <><option value="">All campuses</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</>
            )}
          </select>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:14 }}>
        {cards.map(c => <Card key={c.key} {...c} onClick={() => setDept(c)} />)}
      </div>
    </div>
  );
}
